import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { parseEpsfBarcodePayload } from "./epsfBarcodeParse";
import {
  guestGetReceiveScanSession,
  guestPostReceiveScan,
  guestCloseReceiveScanSession,
  guestCloseReceiveScanSessionBeacon,
} from "../epsfService";
import "../EpsfPages.scss";

/**
 * Mobile companion for kit scanning — camera-first app shell.
 * No CTMS login; authorised by short-lived token / short code from laptop.
 * Live camera needs HTTPS (or localhost). Manual kit entry always available.
 * Refreshing or leaving this page closes the scan session.
 */
export default function ImpPhoneScan() {
  const { code: pathCode } = useParams();
  const [params] = useSearchParams();
  const token = String(pathCode || params.get("t") || params.get("c") || "")
    .trim()
    .toUpperCase();
  const apiBaseOverride = String(params.get("api") || "").trim();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectTimerRef = useRef(null);
  const inputRef = useRef(null);
  const zxingRef = useRef(null);
  const controlsRef = useRef(null);
  const startedRef = useRef(false);
  const sessionLiveRef = useRef(false);

  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [guidance, setGuidance] = useState("");
  const [draft, setDraft] = useState("");
  const [lastSent, setLastSent] = useState("");
  const [busy, setBusy] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [liveCameraOn, setLiveCameraOn] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const [flashOk, setFlashOk] = useState(false);

  const isSecureCtx = useMemo(
    () => typeof window !== "undefined" && Boolean(window.isSecureContext),
    [],
  );

  const canTryLiveCamera = useMemo(
    () =>
      typeof window !== "undefined" &&
      Boolean(navigator.mediaDevices?.getUserMedia) &&
      isSecureCtx,
    [isSecureCtx],
  );

  const getReader = useCallback(() => {
    if (!zxingRef.current) {
      zxingRef.current = new BrowserMultiFormatReader();
    }
    return zxingRef.current;
  }, []);

  const stopCamera = useCallback(() => {
    if (detectTimerRef.current) {
      window.clearInterval(detectTimerRef.current);
      detectTimerRef.current = null;
    }
    try {
      controlsRef.current?.stop();
    } catch {
      /* ignore */
    }
    controlsRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setLiveCameraOn(false);
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  useEffect(() => {
    if (!token) {
      setError(
        "Missing scan link. Open the QR / link from the computer Receive screen.",
      );
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const navEntry =
          typeof performance !== "undefined" &&
          performance.getEntriesByType
            ? performance.getEntriesByType("navigation")[0]
            : null;
        const isReload =
          navEntry?.type === "reload" ||
          (typeof performance !== "undefined" &&
            performance.navigation &&
            Number(performance.navigation.type) === 1);

        if (isReload) {
          await guestCloseReceiveScanSession(token, apiBaseOverride || null);
          if (cancelled) return;
          sessionLiveRef.current = false;
          setReady(false);
          setError(
            "This scan session ended because the page was refreshed. Scan the QR again from the computer.",
          );
          return;
        }

        const res = await guestGetReceiveScanSession(
          token,
          apiBaseOverride || null,
        );
        if (cancelled) return;
        if (!res.success) {
          setError(res.message || "Scan session is not available.");
          return;
        }
        sessionLiveRef.current = true;
        setGuidance(
          res.guidance || "Point the camera at a kit barcode.",
        );
        setReady(true);
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.message ||
              "Cannot reach the API from this phone. Use the same Wi‑Fi and open the QR from Receive.",
          );
        }
      }
    })();

    const endSessionOnLeave = () => {
      if (!sessionLiveRef.current) return;
      sessionLiveRef.current = false;
      guestCloseReceiveScanSessionBeacon(token, apiBaseOverride || null);
    };

    window.addEventListener("pagehide", endSessionOnLeave);
    window.addEventListener("beforeunload", endSessionOnLeave);

    return () => {
      cancelled = true;
      window.removeEventListener("pagehide", endSessionOnLeave);
      window.removeEventListener("beforeunload", endSessionOnLeave);
    };
  }, [token, apiBaseOverride]);

  const sendKit = useCallback(
    async (raw) => {
      if (!token || busy) return;
      const parsed = parseEpsfBarcodePayload(raw);
      if (!parsed.kitNumber) {
        setError("Could not read a kit number from the scan.");
        return;
      }
      setBusy(true);
      setError("");
      try {
        const res = await guestPostReceiveScan({
          accessToken: token,
          kitNumber: parsed.kitNumber,
          batchNumber: parsed.batchNumber,
          expiryDate: parsed.expiryDate,
          rawScanValue: parsed.raw?.slice(0, 500) || null,
          apiBaseOverride: apiBaseOverride || null,
        });
        if (!res.success) {
          setError(res.message || "Failed to send kit.");
          return;
        }
        setLastSent(parsed.kitNumber);
        setSentCount((n) => n + 1);
        setDraft("");
        setFlashOk(true);
        window.setTimeout(() => setFlashOk(false), 900);
        window.setTimeout(() => inputRef.current?.focus(), 0);
      } catch (e) {
        setError(e?.message || "Failed to send kit.");
      } finally {
        setBusy(false);
      }
    },
    [token, busy, apiBaseOverride],
  );

  const startLiveCamera = useCallback(async () => {
    if (!canTryLiveCamera || busy) return;
    setCameraError("");
    stopCamera();
    try {
      let lastRaw = "";
      let lastAt = 0;
      const controls = await getReader().decodeFromConstraints(
        { video: { facingMode: { ideal: "environment" } }, audio: false },
        videoRef.current || undefined,
        async (result, err) => {
          if (err || !result) return;
          const raw = String(result.getText?.() || "").trim();
          if (!raw || busy) return;
          const now = Date.now();
          if (raw === lastRaw && now - lastAt < 2500) return;
          lastRaw = raw;
          lastAt = now;
          await sendKit(raw);
        },
      );
      controlsRef.current = controls;
      setLiveCameraOn(true);
    } catch (err) {
      setLiveCameraOn(false);
      setCameraError(
        err?.message ||
          "Camera permission denied or unavailable. Type the kit number below.",
      );
    }
  }, [canTryLiveCamera, busy, stopCamera, getReader, sendKit]);

  useEffect(() => {
    if (!ready || !canTryLiveCamera || startedRef.current) return undefined;
    startedRef.current = true;
    const t = window.setTimeout(() => {
      startLiveCamera();
    }, 250);
    return () => window.clearTimeout(t);
  }, [ready, canTryLiveCamera, startLiveCamera]);

  return (
    <div className="epsf-phone-scan">
      <div className="epsf-phone-scan__shell">
        <header className="epsf-phone-scan__topbar">
          <div className="epsf-phone-scan__brand">
            <img
              className="epsf-phone-scan__logo"
              src="/aq-mark.png"
              alt="AQ"
            />
            <div>
              <h1>AQ Kit scanner</h1>
              <p>ePSF · Linked to open receipt</p>
            </div>
          </div>
          <div className="epsf-phone-scan__pill" role="status">
            {sentCount}
          </div>
        </header>

        {error ? (
          <div className="epsf-phone-scan__banner epsf-phone-scan__banner--error">
            {error}
          </div>
        ) : null}

        {ready ? (
          <>
            <section className="epsf-phone-scan__stage">
              {canTryLiveCamera ? (
                <>
                  <div
                    className={`epsf-phone-scan__viewfinder${
                      liveCameraOn ? " is-live" : ""
                    }${flashOk ? " is-flash" : ""}`}
                  >
                    <video
                      ref={videoRef}
                      className="epsf-phone-scan__video"
                      muted
                      playsInline
                      autoPlay
                    />
                    <div className="epsf-phone-scan__frame" aria-hidden>
                      <span />
                      <span />
                      <span />
                      <span />
                    </div>
                    {!liveCameraOn ? (
                      <div className="epsf-phone-scan__viewfinder-idle">
                        <p>Starting camera…</p>
                        <button
                          type="button"
                          className="epsf-phone-scan__ghost-btn"
                          disabled={busy}
                          onClick={startLiveCamera}
                        >
                          Enable camera
                        </button>
                      </div>
                    ) : (
                      <p className="epsf-phone-scan__scan-cue">
                        Align barcode in the frame
                      </p>
                    )}
                  </div>
                  <div className="epsf-phone-scan__cam-actions">
                    <button
                      type="button"
                      className="epsf-phone-scan__cam-btn"
                      disabled={busy}
                      onClick={() =>
                        liveCameraOn ? stopCamera() : startLiveCamera()
                      }
                    >
                      {liveCameraOn ? "Pause camera" : "Resume camera"}
                    </button>
                  </div>
                </>
              ) : (
                <div className="epsf-phone-scan__no-camera">
                  <strong>Camera needs a secure link</strong>
                  <p>
                    Open this page from an HTTPS receive link, or type the kit
                    number below.
                  </p>
                </div>
              )}

              {cameraError ? (
                <p className="epsf-phone-scan__hint">{cameraError}</p>
              ) : null}
              {guidance ? (
                <p className="epsf-phone-scan__hint">{guidance}</p>
              ) : null}
            </section>

            <section className="epsf-phone-scan__sheet">
              <label htmlFor="phone-kit-input" className="epsf-phone-scan__label">
                Type kit number
              </label>
              <div className="epsf-phone-scan__compose">
                <input
                  id="phone-kit-input"
                  ref={inputRef}
                  className="epsf-phone-scan__input"
                  value={draft}
                  disabled={busy}
                  autoComplete="off"
                  inputMode="text"
                  enterKeyHint="send"
                  placeholder="Kit number"
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (draft.trim()) sendKit(draft);
                    }
                  }}
                />
                <button
                  type="button"
                  className="epsf-phone-scan__send"
                  disabled={busy || !draft.trim()}
                  onClick={() => sendKit(draft)}
                >
                  {busy ? "…" : "Send"}
                </button>
              </div>
              <div className="epsf-phone-scan__meta" role="status">
                {lastSent ? (
                  <>
                    Last sent <strong>{lastSent}</strong>
                  </>
                ) : (
                  "Kits appear on the computer as you scan"
                )}
              </div>
            </section>
          </>
        ) : !error ? (
          <div className="epsf-phone-scan__loading">Connecting…</div>
        ) : null}
      </div>
    </div>
  );
}
